import type { ImageSourcePropType } from "react-native";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import EmptyState from "../EmptyView";
import ProgressEmptySvg from "../ProgressApplet/progress-empty-state.svg";
import { type OverviewStat, ProgressOverview } from "../ProgressOverview";
import ScrollContainer from "../ScrollContainer";
import {
	LanguageLearningProgressSkeleton,
	type LanguageLearningProgressSkeletonProps,
} from "./LanguageLearningProgressSkeleton";
import {
	type LanguageLearningProficiencyContent,
	type LanguageLearningSkillSection,
	SkillSection,
} from "./SkillSection";

const CONTENT_MAX_WIDTH = 896;

export type LanguageLearningProgressAppletProps = {
	title: string;
	subtitle?: string;
	stats: OverviewStat[];
	sections: LanguageLearningSkillSection[];
	onViewAllItemTypes: (sectionId: string) => void;
	onViewItemTypeDetails?: (questionTypeId: string) => void;
	onStartTest?: (sectionId: string, sectionTitle: string) => void;
	emptyState: {
		title: string;
		subtitle: string;
		cta: string;
		onAction: () => void;
	};
	showEmptyState?: boolean;
	tip?: string;
	tipLoading?: boolean;
	isLoading?: boolean;
	skeletonProps?: LanguageLearningProgressSkeletonProps;
};

export const LanguageLearningProgressApplet = ({
	title,
	subtitle,
	stats,
	sections,
	onViewAllItemTypes,
	onViewItemTypeDetails,
	onStartTest,
	emptyState,
	showEmptyState,
	tip,
	tipLoading,
	isLoading,
	skeletonProps,
}: LanguageLearningProgressAppletProps) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const fillViewport = Boolean(showEmptyState && !isLoading);
	return (
		<ScrollContainer
			style={{ backgroundColor: theme.colors.background }}
			containerProps={{
				maxWidth: CONTENT_MAX_WIDTH,
				...(fillViewport ? { containerStyle: { flex: 1 } } : {}),
			}}
		>
			<Box
				width="100%"
				flex={fillViewport ? 1 : undefined}
				gap={24}
				paddingTop={isMobile ? 0 : 24}
				paddingBottom={isMobile ? 24 : 36}
				paddingHorizontal={isMobile ? 0 : 24}
			>
				{isLoading ? (
					<LanguageLearningProgressSkeleton {...skeletonProps} />
				) : showEmptyState ? (
					<EmptyState
						title={emptyState.title}
						subtitle={emptyState.subtitle}
						cta={emptyState.cta}
						image={ProgressEmptySvg as ImageSourcePropType}
						handleAction={emptyState.onAction}
					/>
				) : (
					<>
						<ProgressOverview
							title={title}
							subtitle={subtitle}
							stats={stats}
							tip={tip}
							tipLoading={tipLoading}
						/>
						{sections.map((section) => (
							<SkillSection
								key={section.id}
								section={section}
								onViewAllItemTypes={onViewAllItemTypes}
								onViewItemTypeDetails={onViewItemTypeDetails}
								onStartTest={onStartTest}
							/>
						))}
					</>
				)}
			</Box>
		</ScrollContainer>
	);
};

export type {
	LanguageLearningProficiencyContent,
	LanguageLearningSkillSection,
};
